// Phase 6 — Date-range picker (Popover + react-day-picker Calendar).
//
// Replaces the antd <RangePicker> on the Reports screen. Presentation only: it is
// fully controlled — the screen owns the {from,to} value and reacts to onChange
// exactly as it did to the antd onChange (see RangePickerChange in the screen).
import * as React from "react";
import { format } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { DateRange } from "react-day-picker";
import { Button } from "ui/button";
import { Calendar } from "ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "ui/popover";
import { cn } from "lib/utils";

export interface DateRangePickerProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  /** Disable dates after today (mirrors the legacy disabledDate). */
  disableFuture?: boolean;
  placeholder?: string;
  className?: string;
  /** How many month panels the calendar shows side by side. Defaults to 2. */
  numberOfMonths?: number;
}

export function DateRangePicker({
  value,
  onChange,
  disableFuture = true,
  placeholder = "From Date – To Date",
  className,
  numberOfMonths = 2,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const label =
    value?.from && value?.to
      ? `${format(value.from, "dd-MM-yyyy")} – ${format(value.to, "dd-MM-yyyy")}`
      : value?.from
      ? format(value.from, "dd-MM-yyyy")
      : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-[240px] justify-start gap-2 font-normal", !value?.from && "text-muted-foreground", className)}
        >
          <CalendarDays className="h-4 w-4" />
          <span className="flex-1 truncate text-left">{label}</span>
          {value?.from && (
            <span
              role="button"
              aria-label="Clear date range"
              className="rounded-sm p-0.5 hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onChange(undefined);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="range"
          selected={value}
          onSelect={onChange}
          numberOfMonths={2}
          disabled={disableFuture ? { after: new Date() } : undefined}
          defaultMonth={value?.from}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePicker;
